"use client";

import { useState, useEffect } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/providers/auth-provider";
import { useQueryClient } from "@tanstack/react-query";
import type { ExtractedProspect } from "@/lib/extract-prospect";

interface VoiceProspectModalProps {
  data: ExtractedProspect;
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export function VoiceProspectModal({ data, open, onClose, onCreated }: VoiceProspectModalProps) {
  const { client } = useAuth();
  const queryClient = useQueryClient();
  const [isCreating, setIsCreating] = useState(false);

  const [company, setCompany] = useState(data.company || "");
  const [firstname, setFirstname] = useState(data.contact_firstname || "");
  const [lastname, setLastname] = useState(data.contact_lastname || "");
  const [phone, setPhone] = useState(data.phone || "");
  const [email, setEmail] = useState(data.email || "");
  const [address, setAddress] = useState(data.address || "");
  const [zip, setZip] = useState(data.zip || "");
  const [city, setCity] = useState(data.city || "");
  const [need, setNeed] = useState(data.need || "");

  useEffect(() => {
    if (!open) return;
    setCompany(data.company || "");
    setFirstname(data.contact_firstname || "");
    setLastname(data.contact_lastname || "");
    setPhone(data.phone || "");
    setEmail(data.email || "");
    setAddress(data.address || "");
    setZip(data.zip || "");
    setCity(data.city || "");
    setNeed(data.need || "");
  }, [open, data]);

  const handleCreate = async () => {
    if (!company.trim()) {
      toast.error("Le nom de la societe est obligatoire");
      return;
    }
    if (!client) return;

    setIsCreating(true);
    try {
      // Create third party as prospect
      const thirdpartyId = await client.createThirdParty({
        name: company,
        client: "2", // prospect
        fournisseur: "0",
        address: address || undefined,
        zip: zip || undefined,
        town: city || undefined,
        phone: phone || undefined,
        email: email || undefined,
        note_private: [
          need ? `Besoin: ${need}` : "",
          data.notes ? `Dictee: ${data.notes}` : "",
        ]
          .filter(Boolean)
          .join("\n\n"),
        stcomm: 0, // Nouveau
      } as any);

      // Create contact if name provided
      if ((firstname || lastname) && thirdpartyId) {
        try {
          await client.createContact({
            socid: String(thirdpartyId),
            firstname: firstname || "",
            lastname: lastname || company,
            phone_mobile: phone || undefined,
            email: email || undefined,
          } as any);
        } catch (contactErr) {
          console.warn("Contact creation failed:", contactErr);
          // Non-blocking: prospect is already created
        }
      }

      queryClient.invalidateQueries({ queryKey: ["crm-prospects"] });
      toast.success(`Prospect "${company}" cree`);
      onCreated();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Erreur inconnue";
      toast.error(`Erreur: ${msg}`);
    } finally {
      setIsCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Nouveau prospect (vocal)</DialogTitle>
        </DialogHeader>

        {data.notes && (
          <div className="bg-gray-50 rounded-lg p-3 text-sm text-gray-600 italic border">
            &laquo; {data.notes} &raquo;
          </div>
        )}

        <div className="space-y-3">
          <div>
            <Label htmlFor="vp-company">Societe *</Label>
            <Input
              id="vp-company"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              placeholder="Nom de la societe"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="vp-firstname">Prenom</Label>
              <Input
                id="vp-firstname"
                value={firstname}
                onChange={(e) => setFirstname(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="vp-lastname">Nom</Label>
              <Input
                id="vp-lastname"
                value={lastname}
                onChange={(e) => setLastname(e.target.value)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="vp-phone">Telephone</Label>
              <Input
                id="vp-phone"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="06 XX XX XX XX"
              />
            </div>
            <div>
              <Label htmlFor="vp-email">Email</Label>
              <Input
                id="vp-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="vp-address">Adresse</Label>
            <Input
              id="vp-address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="vp-zip">Code postal</Label>
              <Input
                id="vp-zip"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="vp-city">Ville</Label>
              <Input
                id="vp-city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="vp-need">Besoin</Label>
            <Input
              id="vp-need"
              value={need}
              onChange={(e) => setNeed(e.target.value)}
              placeholder="Cablage, installation, depannage..."
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isCreating}>
            Annuler
          </Button>
          <Button onClick={handleCreate} disabled={isCreating || !company.trim()}>
            {isCreating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creation...
              </>
            ) : (
              "Creer le prospect"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
